import React, { FC, useCallback } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { CrudActions, CrudEventEnum, EntityTypeEnum, TradingControlLimitDetail, WorkflowActions } from 'api'
import { Locale, Translation } from 'locale'
import { ActionItem, SplitButtonDropdown } from 'view/components/menus'
import { NotificationActions, NotificationType, ShowDialogArgs } from 'store/notification'
import { ActionsButtonProps } from 'view/components/datagrid'
import { ModalConsumerProps, withModal } from 'view/shared/modal'
import { CorrelatedResponseCallback } from 'store/notification/notificationTypes'
import { TradingControlDialog } from '../TradingControlDialog'

export type TradingControlActionsButtonProps = OwnProps & DispatchProps

interface OwnProps extends ModalConsumerProps, ActionsButtonProps<TradingControlLimitDetail> {}

interface StateProps {}

interface DispatchProps {
  onShowTradingControlDialog: (
    entityTypeEnum: EntityTypeEnum,
    entityId: string,
    correlatedResponseCallback?: CorrelatedResponseCallback
  ) => unknown
  onShowDialog: (dialogArgs: ShowDialogArgs) => unknown
}

const mapStateToProps = createStructuredSelector({})

const mapDispatchToProps = {
  onShowTradingControlDialog: WorkflowActions.createEditWorkflow,
  onShowDialog: NotificationActions.showDialog
}

export const TradingControlActionsButtonComponent: FC<TradingControlActionsButtonProps> = props => {
  const { items, showModal, onShowTradingControlDialog, onShowDialog } = props

  const getLabel = (baseLabel: Translation, count: number) => {
    const { isGroupRow } = props
    const countLabel = isGroupRow ? ` (${count})` : ``
    return `${Locale.translate(baseLabel)}${countLabel}`
  }

  const handleShowItemDialog = (entityId: string) => {
    const correlatedResponseCallback = (workflowId: string) => {
      showModal(<TradingControlDialog workflowId={workflowId} entityId={entityId} />)
    }
    onShowTradingControlDialog(EntityTypeEnum.TRADING_CONTROL_LIMIT, entityId, correlatedResponseCallback)
  }

  const handleDelete = useCallback(
    (deleteTradingControlLimit: TradingControlLimitDetail[]) => {
      const titleText = Locale.translate(Translation.TC_PROMPT_DELETE_TRADE_CONTROL_LIMIT, { count: deleteTradingControlLimit.length })

      const messageText =
        deleteTradingControlLimit.length === 1
          ? Locale.translate(Translation.SC_PROMPT_MSG_DELETE_SINGLE_TRADE_CONTROL_LIMIT, {
              name: deleteTradingControlLimit[0].organisation?.legalName
            })
          : Locale.translate(Translation.SC_PROMPT_MSG_DELETE_MULTI_TRADE_CONTROL_LIMIT, { count: deleteTradingControlLimit.length })

      const deleteActions = deleteTradingControlLimit.map(deleteItem =>
        CrudActions.entityDelete(EntityTypeEnum.TRADING_CONTROL_LIMIT, deleteItem.tradingControlLimit.id)
      )

      onShowDialog({
        type: NotificationType.Confirm,
        titleText,
        messageText,
        buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
        defaultButton: Translation.TC_BUTTON_YES,
        cancelButton: Translation.TC_BUTTON_NO,
        responseMap: {
          [Translation.TC_BUTTON_YES]: deleteActions
        }
      })
    },
    [onShowDialog]
  )

  const canEditDetails = (item: TradingControlLimitDetail) => item.availableEvents.includes(CrudEventEnum.UPSERT)
  const canDeleteDetails = (item: TradingControlLimitDetail) => item.availableEvents.includes(CrudEventEnum.DELETE)

  const viewItems = items.filter(item => !canEditDetails(item))
  const editItems = items.filter(item => canEditDetails(item))
  const deleteItems = items.filter(item => canDeleteDetails(item))

  const actions: ActionItem[] = [
    {
      label: getLabel(Translation.TC_ACTION_VIEW_DETAILS, 1),
      onClick: () => handleShowItemDialog(viewItems[0].tradingControlLimit.id),
      enabled: viewItems.length === 1
    },
    {
      label: getLabel(Translation.TC_ACTION_EDIT_DETAILS, 1),
      onClick: () => handleShowItemDialog(editItems[0].tradingControlLimit.id),
      enabled: editItems.length === 1
    },
    {
      label: getLabel(Translation.TC_ACTION_DELETE, deleteItems.length),
      onClick: () => handleDelete(deleteItems),
      enabled: deleteItems.length > 0
    }
  ]

  return <SplitButtonDropdown dataTestId={'TradingControlActionsButton'} actions={actions} maxButtons={1} />
}

export const TradingControlActionsButton = withModal(
  connect<StateProps, DispatchProps, OwnProps>(mapStateToProps, mapDispatchToProps)(TradingControlActionsButtonComponent)
)
